import { t } from '@lingui/core/macro';
import { linkOptions, type NavigationSidebarProps } from '@luminovo/design-system';

import type { PagePermissionContext } from '@/shared/permissions/PagePermissionContext';
import { canViewAdvancedUserManagement } from '@/shared/permissions/pagePermissions';
import { useSession } from '@/shared/permissions/session';

/** What the placement of the menu's entries depends on: a part of the session. */
type OrganizationSettingsAccess = Pick<
    PagePermissionContext,
    'userType' | 'isAdvancedRoleManagementEnabled' | 'hasPermissionForAction'
>;

// Today's navigation only: add a new settings page to `settingsMenu` in `app/navigation/settingsMenu.ts` too.
/**
 * The menu entries in order, also for `beforeLoad`'s redirect. A link shows where its page's route lets the user in,
 * which the design system asks, and a header with no link below it hides; `hidden` here only places an entry.
 */
export function organizationSettingsEntries(access: OrganizationSettingsAccess) {
    const { isAdvancedRoleManagementEnabled: isRoleSettingsEnabled } = access;

    // `satisfies`, not an annotation: each link keeps its route literal for the `beforeLoad` redirect.
    const entries = [
        { type: 'header', label: t`General` },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/users' }),
            label: t`Users`,
            // Both Users entries link to the same page; this condition picks the section, and the route refuses anyone
            // but Internal users.
            hidden: isRoleSettingsEnabled,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/sites' }),
            label: t`Sites`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/branding' }),
            label: t`Branding`,
        },
        {
            type: 'header',
            label: t`User management`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/users' }),
            label: t`Users`,
            // Both Users entries link to the same page; this condition picks the section.
            hidden: !canViewAdvancedUserManagement(access),
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/user-management/roles' }),
            label: t`Additional roles`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/user-management/action-permissions' }),
            label: t`Action permissions`,
        },

        {
            type: 'header',
            label: t`Design`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/assembly-settings' }),
            label: t`Assembly settings`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/component-settings' }),
            label: t`Component settings`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/manufacturer-preferences' }),
            label: t`Manufacturer preferences`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/pcb' }),
            label: t`PCB settings`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/carbon-footprints' }),
            label: t`Carbon footprints`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/custom-part-categories' }),
            label: t`Part categories`,
        },
        {
            type: 'header',
            label: t`Integrations`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/data-integrations' }),
            label: t`Data integrations`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/supplier-api-integrations' }),
            label: t`Supplier integrations`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/api-integration' }),
            label: t`Luminovo API`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/csv-integration' }),
            label: t`CSV integrations`,
        },
        {
            type: 'header',
            label: t`Customer portal`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/customer-portal' }),
            label: t`General`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/customer-portal/customer-specific-settings' }),
            label: t`Customer-specific settings`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/customer-portal/additional-services' }),
            label: t`Additional services`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/customer-portal/payment-credentials' }),
            label: t`Payment credentials`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/customer-portal/checkout-conditions' }),
            label: t`Checkout conditions`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/customer-portal/landing-page' }),
            label: t`Landing page`,
        },
        {
            type: 'header',
            label: t`Other`,
        },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/organization/automatic-rfq-number' }),
            label: t`Automatic project number`,
        },
        { type: 'header', label: t`My settings` },
        {
            type: 'link',
            link: linkOptions({ to: '/settings/user/account-security' }),
            label: t`Account security`,
        },
    ] satisfies NavigationSidebarProps['contents'];

    return entries.filter((entry) => !entry.hidden);
}

/** {@link organizationSettingsEntries} for the signed-in user: the same session the settings forward reads. */
export const useOrganizationSettingsEntries = () => organizationSettingsEntries(useSession());
